import { useReducer, useState } from "react";
import dayjs from "dayjs";
import { Species } from "types/species";
import { User } from "types/user";
import { ACTIONS } from "./config";
import { ErrorType, RequestFeedbackAction, RequestFeedbackData } from "./types";
import { FeedbackStatus } from "types/feedback";
import { REQUIREMENT } from "helpers/constants";

export const useRequestFeedbackData = () => {
  const initialData: RequestFeedbackData = {
    coachAvailable: null,
    observationDate: new Date().toISOString().split("T")[0],
    site: "",
    course: "",
    selectedCoach: null,
    selectedLearner: null,
    mrn: "",
    selectedSpecies: null,
    animalSex: "",
    selectedCompetencySkill: null,
    perform: "",
    coachObserve: "",
    coachRate: null,
    rate: null,
    iterSubRequirementRates: {},
    iterSubRequirementCoachRates: {},
    iterSubRequirementComments: {},
    suggestion: "",
    improvement: "",
    notes: "",
    petName: "",
    name: "",
    caseDate: dayjs(),
    caseTime: dayjs(),
    selectedSite: null,
    selectedFocusFeedback: [],
    status: FeedbackStatus.DRAFT,
  };

  const reducer = (
    state: RequestFeedbackData,
    action: RequestFeedbackAction,
  ): RequestFeedbackData => {
    switch (action.type) {
      case ACTIONS.INITIAL_DATA:
        const { payload } = action;
        const firstFeedbackSkill = payload.feedback_skills?.[0];
        // Detect ITER: if all feedback_skills are competencies (not the ITER requirement itself)
        const isITER = payload.feedback_skills && payload.feedback_skills.length > 0 &&
          payload.feedback_skills.every(
            (skill: any) => skill.requirement?.type === REQUIREMENT.REQUIREMENT_COMPETENCY
          );
        
        // For ITER, collect all sub-requirement ratings and comments
        const iterSubRequirementRates: Record<string, number | null> = {};
        const iterSubRequirementCoachRates: Record<string, number | null> = {};
        const iterSubRequirementComments: Record<string, string> = {};
        
        if (isITER && payload.feedback_skills) {
          payload.feedback_skills.forEach((skill: any) => {
            if (skill.requirement?.requirement_id) {
              iterSubRequirementRates[skill.requirement.requirement_id] = skill.self_rating || null;
              iterSubRequirementCoachRates[skill.requirement.requirement_id] = skill.coach_rating || null;
              // Store each competency's comment individually - don't use perform field
              iterSubRequirementComments[skill.requirement.requirement_id] = skill.student_comments || "";
            }
          });
        }
        
        return {
          ...state,
          observationDate:
            payload.observation_date || new Date().toISOString().split("T")[0],
          course: payload.course || "",
          selectedCoach: payload.coach || null,
          mrn: payload.mrn || "",
          selectedSpecies: payload.species || null,
          animalSex: payload.animal_sex || "",
          selectedCompetencySkill:
            firstFeedbackSkill?.requirement || null,
          // For ITER, don't populate perform - only use individual comments
          perform: isITER ? "" : (firstFeedbackSkill?.student_comments || ""),
          coachObserve: firstFeedbackSkill?.coach_comments || "",
          coachRate: isITER ? null : (firstFeedbackSkill?.coach_rating || null),
          rate: isITER ? null : (firstFeedbackSkill?.self_rating || null),
          iterSubRequirementRates: isITER ? iterSubRequirementRates : state.iterSubRequirementRates,
          iterSubRequirementCoachRates: isITER ? iterSubRequirementCoachRates : state.iterSubRequirementCoachRates,
          iterSubRequirementComments: isITER ? iterSubRequirementComments : state.iterSubRequirementComments,
          suggestion: firstFeedbackSkill?.areas || "",
          improvement: firstFeedbackSkill?.actions || "",
          name: payload.animal_name || "",
          caseDate: payload.observation_date
            ? dayjs(payload.observation_date).startOf("day")
            : null,
          caseTime: payload.observation_date
            ? dayjs(payload.observation_date)
            : null,
          selectedSite: payload.site || null,
          selectedFocusFeedback: payload.tags?.map((tag: any) => tag.tag) || [],
        };
      case ACTIONS.SET_OBSERVATION_DATE:
        return { ...state, observationDate: action.payload };
      case ACTIONS.SET_SITE:
        return { ...state, selectedSite: action.payload };
      case ACTIONS.SET_COURSE:
        return { ...state, course: action.payload };
      case ACTIONS.SET_SELECTED_COACH:
        return { ...state, selectedCoach: action.payload as User };
      case ACTIONS.SET_SELECTED_LEARNER: {
        const nextLearner = action.payload as User;
        const learnerChanged =
          state.selectedLearner?.user_id !== nextLearner?.user_id;
        return {
          ...state,
          selectedLearner: nextLearner,
          selectedCompetencySkill: learnerChanged
            ? null
            : state.selectedCompetencySkill,
        };
      }
      case ACTIONS.SET_MRN:
        return { ...state, mrn: action.payload };
      case ACTIONS.SET_SPECIES:
        return { ...state, selectedSpecies: action.payload as Species };
      case ACTIONS.SET_SEX:
        return { ...state, animalSex: action.payload };
      case ACTIONS.SET_COMPETENCY_SKILLS:
        const selectedRequirement = action.payload;
        const isITERSelected = selectedRequirement?.type === REQUIREMENT.REQUIREMENT_ITER;
        const now = new Date();
        
        return {
          ...state,
          selectedCompetencySkill: selectedRequirement,
          // For ITER, set current date and time by default
          ...(isITERSelected && {
            observationDate: now.toISOString().split("T")[0],
            caseDate: dayjs(now),
            caseTime: dayjs(now),
          }),
        };
      case ACTIONS.SET_PERFORM:
        return { ...state, perform: action.payload };
      case ACTIONS.SET_RATE:
        return { ...state, rate: action.payload };
      case ACTIONS.SET_COACH_OBSERVE:
        return { ...state, coachObserve: action.payload };
      case ACTIONS.SET_ASK:
        return { ...state, ask: action.payload };
      case ACTIONS.SET_DISCUSS:
        return { ...state, discuss: action.payload };
      case ACTIONS.SET_NOTES:
        return { ...state, notes: action.payload };
      case ACTIONS.SET_PET_NAME:
        return { ...state, petName: action.payload };
      case ACTIONS.SET_NAME:
        return { ...state, name: action.payload };
      case ACTIONS.SET_SELECTED_FOCUS_FEEDBACK:
        return { ...state, selectedFocusFeedback: action.payload };
      case ACTIONS.SET_COACH_AVAILABLE:
        return { ...state, coachAvailable: action.payload };
      case ACTIONS.SET_COACH_RATE:
        return { ...state, coachRate: action.payload };
      case ACTIONS.SET_CASE_DATE:
        return { ...state, caseDate: action.payload };
      case ACTIONS.SET_CASE_TIME:
        return { ...state, caseTime: action.payload };
      case ACTIONS.SET_ITER_SUB_REQUIREMENT_RATE:
        return {
          ...state,
          iterSubRequirementRates: {
            ...state.iterSubRequirementRates,
            [action.payload.requirementId]: action.payload.rate,
          },
        };
      case ACTIONS.SET_ITER_SUB_REQUIREMENT_COACH_RATE:
        return {
          ...state,
          iterSubRequirementCoachRates: {
            ...state.iterSubRequirementCoachRates,
            [action.payload.requirementId]: action.payload.rate,
          },
        };
      case ACTIONS.SET_ITER_SUB_REQUIREMENT_COMMENT:
        return {
          ...state,
          iterSubRequirementComments: {
            ...state.iterSubRequirementComments,
            [action.payload.requirementId]: action.payload.comment,
          },
        };
      default:
        return state;
    }
  };

  const [error, setError] = useState<ErrorType>({
    selectedCoach: null,
    selectedLearner: null,
    mrn: null,
    selectedSite: null,
    course: null,
    selectedSpecies: null,
    animalSex: null,
    perform: null,
    coachObserve: null,
    suggestion: null,
    improvement: null,
    selectedCompetencySkill: null,
    petName: null,
    name: null,
    caseDate: null,
    caseTime: null,
    rate: null,
    coachAvailable: null,
    iterSubRequirementRateErrors: {},
    iterSubRequirementCoachRateErrors: {},
    iterSubRequirementCommentErrors: {},
  });
  const [data, dispatch] = useReducer(reducer, initialData);

  return { data, dispatch, error, setError };
};
